import { Box } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useCanvasSize } from '@/hooks/useCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import {
  useGenerateRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import type { LABColor } from '@/store/appSlice'
import {
  setOpaquerRecipeOutput,
  setOpaquerTarget,
  setSelectedOpaquerKey,
} from '@/store/pfmCrown'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  getProtocolValue,
  hasNonEmptyOutputSingleCanvas,
  saveRecipeGeneric,
} from '@/utils/helper'
import { toLabColor } from '@/utils/matisseV2/toLabColor'

import { imageType } from '../../allInOne/utils/constants'
import OpaquerPanel from './panel/OpaquerPanel'

function Opaquer() {
  const { t } = useTranslation('translation', { keyPrefix: 'gingiva' })
  const { t: translation } = useTranslation()

  const dispatch = useDispatch()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const selectedOpaquerKey = useAppSelector(
    (state) => state.pfmCrown.selectedOpaquerKey,
  )
  const { containerRef, dimensions } = useCanvasSize()

  const opaquerCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.opaquerCeramicBrand,
  )

  const sliderValues = useAppSelector(
    (state) => state.pfmCrown.opaquerSliderValue,
  )

  const opaquerRecipe = useAppSelector((state) => state.pfmCrown.opaquerRecipe)

  const [generateRecipe] = useGenerateRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const onImageClick = (
    _type: string,
    _placement: string,
    imagePos: { x: number; y: number },
    _hiddenCanvasRef: any,
    labColor: LABColor,
  ) => {
    if (
      canvasImage?.id &&
      imageCalibrationStatus?.[canvasImage?.id] ===
        ImageCalibrationStatus.CALIBRATING
    ) {
      if (_type === imageType.RAW) {
        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
      return
    }

    if (selectedOpaquerKey) {
      if (!opaquerCeramicBrand) {
        toast.error(t('selectCeramicBrand'))
        return
      }

      dispatch(
        setOpaquerTarget({
          imageId: canvasImage?.id,
          targetcolor: [labColor.L, labColor.a, labColor.b],
          position: imagePos,
          output: {
            [selectedOpaquerKey]: selectedOpaquerKey,
          },
        }),
      )
      toast.info(translation('recipeGenerate.inProgress'))
      const targetcolor = toLabColor(labColor)
      generateRecipe({
        caseId,
        recipePath: 'opaquer',
        data: {
          opaquerbrand: opaquerCeramicBrand,
          ...(targetcolor ? { targetcolor } : {}),
          availspace: Number(sliderValues[selectedOpaquerKey]),
          protocol: getProtocolValue(canvasImage),
        },
      })
        .unwrap()
        .then((res) => {
          toast.success(t('recipeGenerated'))
          const { generation_id, ...output } = res ?? {}
          dispatch(
            setOpaquerRecipeOutput({
              imageId: canvasImage?.id,
              key: selectedOpaquerKey,
              result: output,
              generation_id,
            }),
          )
        })
        .catch((error) => {
          toast.error(error?.data?.message || t('recipeGenerationFailed'))
        })
      dispatch(setSelectedOpaquerKey(''))
    }
  }

  const onSaveAsImage = () => {
    generateImage(
      combinedRef as React.RefObject<HTMLDivElement>,
      'Opaquer-Recipe.png',
    )
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: opaquerRecipe[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      closeDialog: () => setSaveRecipedialogOpen(false),
      recipeType: 'opaquer',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: opaquerRecipe[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'opaquer',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      rightPanel={
        <OpaquerPanel
          containerRef={containerRef as React.RefObject<HTMLDivElement>}
        />
      }
      disabled={hasNonEmptyOutputSingleCanvas(opaquerRecipe, canvasImage?.id)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          ref={containerRef}
          display="flex"
          flexGrow={1}
          flexDirection={'column'}
        >
          <WorkFlowTab width={dimensions.width} />
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={canvasImage?.type}
                imageUrl={
                  getDehazedImageUrl(canvasImage) ||
                  canvasImage?.latest_image ||
                  canvasImage?.calibrated_image ||
                  canvasImage?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                onImageClick={onImageClick}
                imageId={canvasImage?.id}
                combinedRef={combinedRef}
                rawWidth={canvasImage?.raw_width}
                rawHeight={canvasImage?.raw_height}
                isActiveCircle={!!selectedOpaquerKey}
              />
            </>
          ) : (
            <ImageEdit selectedImage={canvasImage} />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default Opaquer
